import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { ColDef } from "ag-grid-enterprise";
import { useDropzone } from "react-dropzone";
import { SaveUpdateContextMenu } from "../../api/preworkDetailApi/preworkDetailApi";
import { SaveUpdateDeleteExcelData } from "../../api/emailTemplateApi/emailTemplateApi";
import { useSnackbar } from "../Common/SnackbarContext";
import { urlConfig } from "../../config/urlConfig";
import { Col, Form, Row } from "react-bootstrap";
import { getFoldersByLevels } from "../../api/documentsApi/documentsApi";
import LocalStorageUtil from "../../utils/localStorage";

interface FileUploadsProps {
  isOpen: boolean;
  onClose: (isCancelClick: boolean) => void;
  title: string;
  selectedData: any;
  screenType: string;
  isOtherOption?: boolean; // Optional prop to handle other options
}

const FileUploads: React.FC<FileUploadsProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
  screenType,
  isOtherOption = false,
}) => {
  const { showSnackbar } = useSnackbar();
  const [rowData, setRowData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [files, setFiles] = useState<FileList | null>(null);
  const [folderLevelParent, setFolderLevelParent] = useState<any[]>([]);
  const [folderLevelChild, setFolderLevelChild] = useState<any[]>([]);
  const [folderLevel3, setFolderLevel3] = useState<any[]>([]);
  const [folderLevel4, setFolderLevel4] = useState<any[]>([]);
  const [folderLevel5, setFolderLevel5] = useState<any[]>([]);
  const [folderLevel6, setFolderLevel6] = useState<any[]>([]);
  const [folderLevel1Value, setFolderLevel1Value] = useState<number>(0);
  const [folderLevel2Value, setFolderLevel2Value] = useState<number>(0);
  const [folderLevel3Value, setFolderLevel3Value] = useState<number>(0);
  const [folderLevel4Value, setFolderLevel4Value] = useState<number>(0);
  const [folderLevel5Value, setFolderLevel5Value] = useState<number>(0);
  const [folderLevel6Value, setFolderLevel6Value] = useState<number>(0);
  const [folderLevel1Text, setFolderLevel1Text] = useState<string>("");
  const [folderLevel2Text, setFolderLevel2Text] = useState<string>("");
  const [folderLevel3Text, setFolderLevel3Text] = useState<string>("");
  const [folderLevel4Text, setFolderLevel4Text] = useState<string>("");
  const [folderLevel5Text, setFolderLevel5Text] = useState<string>("");
  const [folderLevel6Text, setFolderLevel6Text] = useState<string>("");

  //Action Cell Renderer
  const actionCellRenderer = (params: any) => {
    if (params.data === undefined) {
      return "";
    } else {
      return (
        <button
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "var(--aggrid-text-color)",
            display: "flex",
            alignItems: "center",
          }}
          onClick={() => handleDeleteRow(params.data.fileName)}
        >
          <CloseIcon />
        </button>
      );
    }
  };

  //Defined grid column
  const [columnDef] = useState<ColDef[]>([
    {
      headerName: "File Name",
      field: "fileName",
      sortable: true,
      filter: true,
      resizable: true,
      width: 250,
    },
    {
      headerName: "File Size",
      field: "fileSize",
      sortable: true,
      filter: true,
      resizable: true,
      width: 150,
    },
    {
      headerName: "Action",
      field: "action",
      width: 110,
      sortable: false,
      resizable: false,
      cellRenderer: actionCellRenderer,
    },
  ]);

  //Handle delete row
  const handleDeleteRow = (fileName: string) => {
    setRowData((prevRowData) =>
      prevRowData.filter((row) => row.fileName !== fileName)
    );
  };

  //Handle Add button click
  async function uploadPreworkDetails({
    files,
    selectedData,
    showSnackbar,
    onClose,
    urlConfig,
  }: any) {
    const formData = new FormData();
    formData.append("paramGetUrl", `${urlConfig.PreDetailsUploadToServer}`);
    formData.append(
      "ParentDocumentID",
      selectedData.parentDocumentID === undefined
        ? selectedData.DocumentID
        : selectedData.parentDocumentID
    );
    formData.append("ParentDocumentStatus", selectedData.parentDocumentStatus);
    formData.append(
      "CreatedBy",
      LocalStorageUtil.getItem("userId")?.toString() ?? ""
    );
    formData.append(
      "AccountId",
      LocalStorageUtil.getItem("accountId")?.toString() ?? ""
    );
    formData.append(
      "FileFormat",
      selectedData.screenType?.toLowerCase() === "advancesearch"
        ? ".pdf,.xls,.xlsx,.zip"
        : ".pdf"
    );
    formData.append(
      "contextName",
      LocalStorageUtil.getItem("contextName") ?? ""
    );
    if (files) {
      Array.from(files).forEach((file, index) => {
        formData.append(`file${index}`, file as Blob);
      });
    }
    const response = await SaveUpdateContextMenu(formData);
    if (response.inValidFiles === "") {
      showSnackbar("File Successfully Uploaded");
    } else {
      showSnackbar("Sorry, Failed to upload (1) document(s) .", true);
    }
    onClose(false);
  }

  async function uploadEmailTemplate({
    files,
    showSnackbar,
    onClose,
    urlConfig,
  }: any) {
    const formData = new FormData();
    formData.append("paramGetUrl", `${urlConfig.SaveUpdateDeleteExcelData}`);
    formData.append(
      "AccountId",
      LocalStorageUtil.getItem("accountId")?.toString() ?? ""
    );
    formData.append(
      "CreatedBy",
      LocalStorageUtil.getItem("userId")?.toString() ?? ""
    );
    formData.append("FileFormat", ".xls,.xlsx");
    formData.append(
      "contextName",
      LocalStorageUtil.getItem("contextName") ?? ""
    );
    formData.append(
      "BusinessUnitId",
      LocalStorageUtil.getItem("businessUnitId")?.toString() ?? ""
    );
    if (files) {
      Array.from(files).forEach((file, index) => {
        formData.append(`file${index}`, file as Blob);
      });
    }
    const response = await SaveUpdateDeleteExcelData(formData);
    if (response.inValidFiles == "") {
      showSnackbar("File Successfully Uploaded");
    } else {
      showSnackbar("Sorry, Failed to upload (1) document(s) .", true);
    }
    onClose(false);
  }

  async function uploadFileLocation({
    files,
    showSnackbar,
    onClose,
    urlConfig,
  }: any) {
    const formData = new FormData();
    formData.append("paramGetUrl", `${urlConfig.FileLocationUpload}`);
    formData.append(
      "AccountId",
      LocalStorageUtil.getItem("accountId")?.toString() ?? ""
    );
    formData.append(
      "CreatedBy",
      LocalStorageUtil.getItem("userId")?.toString() ?? ""
    );
    formData.append("FileFormat", ".xls,.xlsx");
    formData.append(
      "contextName",
      LocalStorageUtil.getItem("contextName") ?? ""
    );
    if (files) {
      Array.from(files).forEach((file, index) => {
        formData.append(`file${index}`, file as Blob);
      });
    }
    const response = await SaveUpdateContextMenu(formData);
    if (response === "") {
      showSnackbar("File Successfully Uploaded");
    } else {
      showSnackbar("Sorry, Failed to upload (1) document(s) .", true);
    }
    onClose(false);
  }

  async function uploadDocument({
    files,
    selectedData,
    showSnackbar,
    onClose,
    urlConfig,
    getLastSelectedFolderLevelId,
    folderLevel1Text,
    folderLevel2Text,
    folderLevel3Text,
    folderLevel4Text,
    folderLevel5Text,
    folderLevel6Text,
    constructToLocation,
  }: any) {
    const formData = new FormData();
    const newFolderLevel = getLastSelectedFolderLevelId();
    formData.append("paramGetUrl", `${urlConfig.DocumentFileUpload}`);
    formData.append("FolderId", Number(newFolderLevel).toString());
    formData.append("AccountId", selectedData.accountId);
    formData.append(
      "CreatedBy",
      LocalStorageUtil.getItem("userId")?.toString() ?? ""
    );
    formData.append(
      "contextName",
      LocalStorageUtil.getItem("contextName") ?? ""
    );
    formData.append("Level1", folderLevel1Text);
    formData.append("Level2", folderLevel2Text);
    formData.append("Level3", folderLevel3Text);
    formData.append("Level4", folderLevel4Text);
    formData.append("Level5", folderLevel5Text);
    formData.append("Level6", folderLevel6Text);
    formData.append("ToLocation", constructToLocation());
    if (files) {
      Array.from(files).forEach((file, index) => {
        formData.append(`file${index}`, file as Blob);
      });
    }
    const response = await SaveUpdateContextMenu(formData);
    if (response.returnData === "Upload Success") {
      showSnackbar("File Successfully Uploaded");
    } else {
      showSnackbar(
        `Sorry, Failed to upload ${files?.length} document(s) .`,
        true
      );
    }
    onClose(false);
  }

  const handelAddClick = async () => {
    if (screenType.toLowerCase() === "emailtemplate" && rowData.length > 1) {
      showSnackbar("Please note only one file can be uploaded.", true);
      return;
    }
    setLoading(true);
    try {
      if (rowData.length > 0) {
        setValidationMessage(null);
        const type = screenType.toLowerCase();
        const uploadProps = {
          files,
          selectedData,
          showSnackbar,
          onClose,
          urlConfig,
          getLastSelectedFolderLevelId,
          folderLevel1Text,
          folderLevel2Text,
          folderLevel3Text,
          folderLevel4Text,
          folderLevel5Text,
          folderLevel6Text,
          constructToLocation,
        };
        if (type === "preworkdetails" || type === "advancesearch") {
          await uploadPreworkDetails(uploadProps);
        } else if (type === "emailtemplate") {
          await uploadEmailTemplate(uploadProps);
        } else if (type === "filelocation") {
          await uploadFileLocation(uploadProps);
        } else if (type === "document") {
          await uploadDocument(uploadProps);
        }
      } else {
        showSnackbar("Please upload the file(s)");
      }
    } catch (error) {
      onClose(false);
      showSnackbar("Sorry, Failed to upload (1) document(s) .", true);
      console.log("Error in handelAddClick", error);
    } finally {
      setLoading(false);
    }
  };

  const getLastSelectedFolderLevelId = () => {
    if (folderLevel6Value) return folderLevel6Value;
    if (folderLevel5Value) return folderLevel5Value;
    if (folderLevel4Value) return folderLevel4Value;
    if (folderLevel3Value) return folderLevel3Value;
    if (folderLevel2Value) return folderLevel2Value;
    if (folderLevel1Value) return folderLevel1Value;
    return 0; // Return null if no folder level is selected
  };

  const constructToLocation = () => {
    const folderLevels = [
      folderLevel1Text,
      folderLevel2Text,
      folderLevel3Text,
      folderLevel4Text,
      folderLevel5Text,
      folderLevel6Text,
    ];

    // Filter out empty or undefined folder levels and join them with `/`
    return folderLevels.filter((level) => level).join("/");
  };
  //Handle select files using open file dialog box
  const getValidFileExtensions = (screenType: string) => {
    return screenType.toLowerCase() === "preworkdetails"
      ? [".xls", ".xlsx", ".pdf"]
      : [".xls", ".xlsx"];
  };

  const getValidationMessage = (screenType: string) => {
    return screenType.toLowerCase() === "preworkdetails"
      ? "Please select valid file types(.xls,.xlsx,.pdf)"
      : "Please select valid file types(.xls,.xlsx)";
  };

  const filterValidFiles = (files: FileList, allowedExtensions: string[]) => {
    if (isOtherOption) {
      return Array.from(files);
    }

    return Array.from(files).filter((file) =>
      allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    );
  };

  const processSelectedFiles = (
    validFiles: File[],
    originalFiles: FileList
  ) => {
    const newRowData = validFiles.map((file) => ({
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    }));

    if (newRowData.length > 0) {
      setRowData((prevRowData) => [...prevRowData, ...newRowData]);
      setFiles(originalFiles);
      setValidationMessage(null);
    } else {
      setValidationMessage(getValidationMessage(screenType));
    }
  };

  const handelSelectFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const allowedExtensions = getValidFileExtensions(screenType);
    const validFiles = filterValidFiles(files, allowedExtensions);
    processSelectedFiles(validFiles, files);
  };

  //Handle close button click
  const handleCloseClick = () => {
    onClose(true);
  };

  //#region Drag and drop file upload
  const onDrop = (files: any) => {
    if (screenType.toLowerCase() === "preworkdetails") {
      const excelFiles = isOtherOption
        ? Array.from(files)
        : files.filter(
            (file: any) =>
              file.name.toLowerCase().endsWith(".xls") ||
              file.name.toLowerCase().endsWith(".xlsx") ||
              file.name.toLowerCase().endsWith(".pdf")
          );
      const newRowData = excelFiles.map((file: any) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }));
      if (newRowData.length > 0) {
        setRowData((prevRowData) => [...prevRowData, ...newRowData]);
        setValidationMessage(null);
      } else {
        setValidationMessage("Please select valid file types(.xls,.xlsx,.pdf)");
      }
    } else {
      const excelFiles = isOtherOption
        ? Array.from(files)
        : files.filter(
            (file: any) =>
              file.name.toLowerCase().endsWith(".xls") ||
              file.name.toLowerCase().endsWith(".xlsx")
          );
      const newRowData = excelFiles.map((file: any) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }));
      if (newRowData.length > 0) {
        setRowData((prevRowData) => [...prevRowData, ...newRowData]);
        setFiles(files);
        setValidationMessage(null);
      } else {
        setValidationMessage("Please select valid file types(.xls,.xlsx)");
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/pdf": [".pdf"],
    },
  });

  //#endregion

  useEffect(() => {
    if (isOtherOption) {
      getParentFolderDetails();
    }
  }, []);
  //#endregion

  const getParentFolderDetails = async () => {
    const parentMenuId = selectedData.depthPath[1];
    try {
      const param: any = {
        FundFolderId: null,
        accountId: null,
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };

      const response = await getFoldersByLevels(param);
      const filterResponse = response.filter(
        (f: any) => f.fundFolderId === Number(parentMenuId)
      );
      setFolderLevelParent(filterResponse);
      getFolderDetailsByFolderId(filterResponse[0]?.fundFolderId);
      setFolderLevel1Text(filterResponse[0]?.folderName);
      setFolderLevel1Value(filterResponse[0]?.fundFolderId);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const getFolderDetailsByFolderId = async (fundFolderId: string) => {
    try {
      const param: any = {
        FundFolderId: Number(fundFolderId) || null,
        accountId: Number(selectedData.accountId),
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };

      const response = await getFoldersByLevels(param);
      setFolderLevelChild(response);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const handleFundFolder2Change = async (event: any) => {
    try {
      setFolderLevel2Text(
        event.target.options[event.target.selectedIndex].text
      );
      setFolderLevel2Value(event.target.value);
      const param: any = {
        FundFolderId: Number(event.target.value) || null,
        accountId: Number(selectedData.accountId),
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };
      const response = await getFoldersByLevels(param);
      setFolderLevel3(response);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const handleFundFolder3Change = async (event: any) => {
    try {
      setFolderLevel3Text(
        event.target.options[event.target.selectedIndex].text
      );
      setFolderLevel3Value(event.target.value);
      const param: any = {
        FundFolderId: Number(event.target.value) || null,
        accountId: Number(selectedData.accountId),
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };
      const response = await getFoldersByLevels(param);
      setFolderLevel4(response);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const handleFundFolder4Change = async (event: any) => {
    try {
      setFolderLevel4Text(
        event.target.options[event.target.selectedIndex].text
      );
      setFolderLevel4Value(event.target.value);
      const param: any = {
        FundFolderId: Number(event.target.value) || null,
        accountId: Number(selectedData.accountId),
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };
      const response = await getFoldersByLevels(param);
      setFolderLevel5(response);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const handleFundFolder5Change = async (event: any) => {
    try {
      setFolderLevel5Text(
        event.target.options[event.target.selectedIndex].text
      );
      setFolderLevel5Value(event.target.value);
      const param: any = {
        FundFolderId: Number(event.target.value) || null,
        accountId: Number(selectedData.accountId),
        contextName: LocalStorageUtil.getItem("contextName") ?? "",
      };
      const response = await getFoldersByLevels(param);
      setFolderLevel6(response);
    } catch (error) {
      console.log("Error in useEffect", error);
    }
  };

  const handleFundFolder6Change = (event: any) => {
    setFolderLevel6Text(event.target.options[event.target.selectedIndex].text);
    setFolderLevel6Value(event.target.value);
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseClick}
        maxWidth="xl"
        PaperProps={{
          style: {
            height: "75%",
            minWidth: "25%",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--primary-color)",
            color: "var(--aggrid-text-color)",
            fontSize: "14px",
            height: "4em",
            padding: "4px 24px",
          }}
        >
          <div
            className="col-sm-12"
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            }}
          >
            <div
              className="col-sm-11"
              style={{
                display: "flex",
                alignItems: "left",
                fontSize: "1em",
                color: "var(--aggrid-text-color)",
              }}
            >
              {title}
            </div>
            <div
              className="col-sm-1"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "right",
              }}
            >
              {" "}
              <Tooltip title="Close">
                <IconButton
                  onClick={handleCloseClick}
                  style={{
                    cursor: "pointer",
                    alignItems: "flex-end",
                    color: "var(--aggrid-text-color)",
                    width: "20px",
                  }}
                >
                  <CloseIcon onClick={handleCloseClick} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "60%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div>
              <div style={{ marginLeft: "1em", textAlign: "center" }}>
                <input
                  type="file"
                  multiple
                  onChange={handelSelectFiles}
                  style={{
                    display: "none",
                  }}
                  id="file-upload-input"
                />
                <label htmlFor="file-upload-input">
                  <Button
                    component="span"
                    style={{
                      textTransform: "none",
                      borderRadius: "0px",
                      backgroundColor: "var(--button-background-color)",
                      color: "var(--button-color)",
                      fontSize: "11px",
                      border: "1px solid #144979",
                      margin: "1em 1em 2em -1em",
                      whiteSpace: "white-space",
                      width: "100%",
                      height: "25px",
                    }}
                  >
                    Select file(s)
                  </Button>
                </label>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                }}
              >
                <InputLabel
                  style={{
                    marginBottom: "5px",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  [or]
                </InputLabel>
              </div>
              <div
                {...getRootProps()}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                  border: "1px dashed #144979",
                  height: "100px",
                }}
              >
                <input {...getInputProps()} />
                <InputLabel
                  style={{
                    marginBottom: "5px",
                    fontWeight: 800,
                    fontSize: "11px",
                  }}
                >
                  Drop files here
                </InputLabel>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                }}
              >
                {validationMessage && (
                  <Typography color="error" style={{ marginTop: "10px" }}>
                    {validationMessage}
                  </Typography>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "20px",
                  height: "7em",
                }}
              >
                <div
                  className="ag-theme-quartz-dark"
                  style={{
                    height: "20vh",
                    width: "99%",
                    marginTop: "90px",
                  }}
                >
                  <AgGridReact
                    rowData={rowData}
                    columnDefs={columnDef}
                    headerHeight={25}
                    gridOptions={{
                      rowSelection: {
                        mode: "multiRow",
                        enableClickSelection: false,
                      },
                    }}
                  />
                </div>
              </div>
              {isOtherOption && (
                <div
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "6.5em",
                  }}
                >
                  <h5
                    style={{
                      fontSize: ".9em",
                      marginTop: "5em",
                      color: "#134684",
                      fontWeight: "bold",
                      paddingBottom: ".5em",
                      borderBottom: "1px dashed #adc6de",
                    }}
                  >
                    CHOOSE FILE PATH
                  </h5>
                  <Row className="mb12">
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel1"
                          disabled={true}
                          value={folderLevel1Value}
                          style={{ fontSize: "11px" }}
                        >
                          {folderLevelParent.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel2"
                          value={folderLevel2Value}
                          onChange={(event) => handleFundFolder2Change(event)}
                          style={{ fontSize: "11px" }}
                        >
                          <option value="">Choose level 2...</option>
                          {folderLevelChild.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb12" style={{ marginTop: ".5em" }}>
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel3"
                          value={folderLevel3Value}
                          onChange={(event) => handleFundFolder3Change(event)}
                          style={{ fontSize: "11px" }}
                        >
                          <option value="">Choose level 3...</option>
                          {folderLevel3.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel4"
                          value={folderLevel4Value}
                          onChange={(event) => handleFundFolder4Change(event)}
                          style={{ fontSize: "11px" }}
                        >
                          <option value="">Choose level 4...</option>
                          {folderLevel4.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb12" style={{ marginTop: ".5em" }}>
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel5"
                          value={folderLevel5Value}
                          onChange={(event) => handleFundFolder5Change(event)}
                          style={{ fontSize: "11px" }}
                        >
                          <option value="">Choose level 5...</option>
                          {folderLevel5.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formCustomer">
                        <Form.Select
                          name="folderLevel6"
                          value={folderLevel6Value}
                          onChange={(event) => handleFundFolder6Change(event)}
                          style={{ fontSize: "11px" }}
                        >
                          <option value="">Choose level 6...</option>
                          {folderLevel6.map((folderLevel: any) => (
                            <option
                              key={folderLevel.fundFolderId}
                              value={folderLevel.fundFolderId}
                            >
                              {folderLevel.folderName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handelAddClick}
            style={{
              textTransform: "none",
              borderRadius: "0px",
              backgroundColor: "var(--button-background-color)",
              color: "var(--button-color)",
              fontSize: "11px",
              padding: "5px 12px",
              border: "1px solid #144979",
              margin: "0 5px 5px 5px",
              whiteSpace: "white-space",
              height: "25px",
            }}
          >
            Add
          </Button>
          <Button
            style={{
              textTransform: "none",
              borderRadius: "0px",
              backgroundColor: "var(--button-background-color)",
              color: "var(--button-color)",
              fontSize: "11px",
              padding: "5px 12px",
              border: "1px solid #144979",
              margin: "0 5px 5px 5px",
              whiteSpace: "white-space",
              height: "25px",
            }}
            onClick={handleCloseClick}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default FileUploads;
